import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceError } from "../../utils/ResourceError";
import { PMDialog } from "../../utils/PMDialog";
import RoleOnly from "../../../routes/roleOnly";
import { useSettings } from "../../../contexts/settings-context";
import {
  acceptOfferAsync,
  approveOfferRevisionAsync,
  getOfferAsync,
  getOfferRevisionsAsync,
  rejectOfferRevisionAsync,
} from "../../../slices/offer";
import {
  isOfferFeatureEnabled,
  isOfferNotAuthorized,
  resolveOfferActionErrorMessage,
} from "./utils";
import { useIsDevOrSuperAdmin } from "../../../utils/useIsDevOrSuperAdmin";

const DG_WARNING_THRESHOLD = 25;
const ACCEPTABLE_STATUSES = ["sent", "issued", "approved"];
/** Includes pending_review — API returns it to draft on reject. */
const REJECTABLE_STATUSES = ["pending_review", "approved", "issued", "sent"];
const APPROVABLE_STATUSES = ["draft", "pending_review"];

function revisionStatusLabel(t, status) {
  const key = `offers.history.revision_status.${status || "draft"}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

function formatMoney(priceFormatter, value) {
  if (value == null || value === "") return "—";
  const num = parseFloat(value);
  if (Number.isNaN(num)) return String(value);
  if (priceFormatter?.format) return priceFormatter.format(num);
  return num.toLocaleString("nb-NO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatPercent(value) {
  if (value == null || value === "") return "—";
  const num = parseFloat(value);
  if (Number.isNaN(num)) return String(value);
  return `${num.toLocaleString("nb-NO", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

/**
 * Godkjenning tab — intern godkjenning + kundeaksept i to seksjoner.
 * Bak offer_revisions. Intern approve låser uten auto-ny draft.
 */
export default function OfferApprovalTab({
  offer,
  refreshKey = 0,
  onSubmitForReview,
  onSendOffer,
  /** Opens shared OfferCreateRevisionDialog (same as header ⋮ menu) */
  onCreateRevision,
  /** @deprecated use onSubmitForReview — kept for soft compat */
  onSend,
}) {
  const handleSubmitForReview = onSubmitForReview || onSend;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { priceFormatter } = useSettings();
  const showDevHints = useIsDevOrSuperAdmin();
  const offerFeatureFlags = useSelector(
    (state) => state.offers?.offerFeatureFlags || {}
  );
  const {
    revisions: storeRevisions,
    revisionsOfferId,
    revisionsLoading,
    revisionsError,
    acceptedRevisionId: storeAcceptedId,
  } = useSelector((state) => ({
    revisions: state.offers?.revisions || [],
    revisionsOfferId: state.offers?.revisionsOfferId,
    revisionsLoading: state.offers?.revisionsLoading,
    revisionsError: state.offers?.revisionsError,
    acceptedRevisionId: state.offers?.acceptedRevisionId,
  }));

  const revisions =
    revisionsOfferId === offer?.id ? storeRevisions : [];
  const featureFlags = offer?.offer_feature_flags || offerFeatureFlags || {};
  const revisionsEnabled = isOfferFeatureEnabled(featureFlags, "offer_revisions");
  const acceptedRevisionId =
    storeAcceptedId ?? offer?.accepted_revision_id ?? null;

  const [busyAction, setBusyAction] = useState(null);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [confirmAcceptOpen, setConfirmAcceptOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState("");

  const loadRevisions = useCallback(() => {
    if (!offer?.id || !revisionsEnabled) return;
    dispatch(getOfferRevisionsAsync(offer.id));
  }, [dispatch, offer?.id, revisionsEnabled]);

  useEffect(() => {
    loadRevisions();
  }, [
    loadRevisions,
    refreshKey,
    offer?.current_draft_revision_id,
    offer?.accepted_revision_id,
  ]);

  const sortedRevisions = useMemo(
    () =>
      [...revisions].sort(
        (a, b) => (b.revision_number || 0) - (a.revision_number || 0)
      ),
    [revisions]
  );

  const draftRevision = useMemo(() => {
    const draftId = offer?.current_draft_revision_id;
    if (draftId) {
      return sortedRevisions.find((r) => r.id === draftId) || null;
    }
    return (
      sortedRevisions.find((r) => APPROVABLE_STATUSES.includes(r?.status)) ||
      null
    );
  }, [offer?.current_draft_revision_id, sortedRevisions]);

  const approvableRevision = useMemo(() => {
    if (draftRevision && APPROVABLE_STATUSES.includes(draftRevision.status)) {
      return draftRevision;
    }
    return (
      sortedRevisions.find((r) => APPROVABLE_STATUSES.includes(r?.status)) ||
      null
    );
  }, [draftRevision, sortedRevisions]);

  const rejectableRevision = useMemo(
    () =>
      sortedRevisions.find((r) =>
        REJECTABLE_STATUSES.includes(r?.status)
      ) || null,
    [sortedRevisions]
  );

  const acceptableRevision = useMemo(() => {
    const candidate = sortedRevisions.find((r) =>
      ACCEPTABLE_STATUSES.includes(r?.status)
    );
    if (!candidate) return null;
    if (acceptedRevisionId && acceptedRevisionId === candidate.id) return null;
    if (candidate.status === "accepted") return null;
    return candidate;
  }, [sortedRevisions, acceptedRevisionId]);

  const latestLocked = useMemo(
    () =>
      sortedRevisions.find(
        (r) =>
          r?.locked ||
          (r?.status &&
            !["draft", "pending_review"].includes(r.status))
      ) || null,
    [sortedRevisions]
  );

  const needsReopen =
    !approvableRevision &&
    latestLocked &&
    ["approved", "rejected", "sent", "issued", "accepted", "superseded"].includes(
      latestLocked.status
    );

  const metricsSource = latestLocked || draftRevision || offer || {};
  const offerTotal =
    metricsSource.total_ex_tax ??
    metricsSource.subtotal_amount ??
    offer?.subtotal_amount ??
    null;
  const costTotal =
    metricsSource.cost_total ?? offer?.cost_total ?? null;
  const contributionMargin =
    metricsSource.contribution_margin ?? offer?.contribution_margin ?? null;
  const dgPercent =
    metricsSource.contribution_margin_percent ??
    offer?.contribution_margin_percent ??
    null;
  const discountTotal =
    metricsSource.discount_total ?? offer?.discount_total ?? null;

  const hasCalculations = (offer?.calculations || []).length > 0;
  const hasOfferItems =
    (Array.isArray(offer?.items) && offer.items.length > 0) ||
    Number(offer?.items_count) > 0;
  const hasPresentationTotals =
    offerTotal != null &&
    offerTotal !== "" &&
    !Number.isNaN(parseFloat(offerTotal)) &&
    parseFloat(offerTotal) !== 0;

  const warnings = useMemo(() => {
    const items = [];
    const dg = parseFloat(dgPercent);
    if (!Number.isNaN(dg) && dg < DG_WARNING_THRESHOLD) {
      items.push({
        id: "low_dg",
        tone: "amber",
        text: t("offers.approval.warning.low_dg", {
          value: formatPercent(dg),
          threshold: DG_WARNING_THRESHOLD,
        }),
      });
    }
    const margin = parseFloat(contributionMargin);
    if (!Number.isNaN(margin) && margin < 0) {
      items.push({
        id: "negative_margin",
        tone: "rose",
        text: t("offers.approval.warning.negative_margin"),
      });
    }

    // Draft revision_lines stay empty until approve/send materializes them
    // (ADR-005 / REV-001). Prefer kalkyle / offer_items / totals over lines_count.
    const linesCount = draftRevision?.lines_count;
    const revisionLinesEmpty =
      draftRevision && (linesCount === 0 || linesCount === "0" || linesCount == null);
    if (revisionLinesEmpty) {
      if (hasCalculations || hasOfferItems || hasPresentationTotals) {
        // ADR / materialize wording — DEV or super admin only
        if (showDevHints) {
          items.push({
            id: "lines_materialize",
            tone: "slate",
            text: t("offers.approval.warning.lines_materialize_on_approve"),
          });
        }
      } else {
        items.push({
          id: "no_lines",
          tone: "amber",
          text: t("offers.approval.warning.no_lines"),
        });
      }
    }
    return items;
  }, [
    dgPercent,
    contributionMargin,
    draftRevision,
    hasCalculations,
    hasOfferItems,
    hasPresentationTotals,
    showDevHints,
    t,
  ]);

  const refreshAfterMutation = async () => {
    if (!offer?.id) return;
    await dispatch(getOfferAsync(offer.id));
    loadRevisions();
  };

  const closeRejectDialog = () => {
    if (busyAction === "reject") return;
    setRejectDialogOpen(false);
    setRejectReason("");
    setRejectReasonError("");
  };

  const handleApproveConfirm = async () => {
    if (!offer?.id || !approvableRevision?.id || busyAction != null) return;
    setBusyAction("approve");
    try {
      const result = await dispatch(
        approveOfferRevisionAsync({
          offerId: offer.id,
          revisionId: approvableRevision.id,
        })
      );
      if (approveOfferRevisionAsync.rejected.match(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result.payload,
            result.payload?.missing
              ? "offers.approval.api_unavailable"
              : "offers.approval.approve_failed"
          )
        );
        return;
      }
      toast.success(
        t("offers.approval.approve_success", {
          number: approvableRevision.revision_number,
        })
      );
      setConfirmApproveOpen(false);
      await refreshAfterMutation();
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.approval.approve_failed")
      );
    } finally {
      setBusyAction(null);
    }
  };

  const handleRejectConfirm = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      setRejectReasonError(t("offers.approval.reject_reason_required"));
      return;
    }
    if (!offer?.id || !rejectableRevision?.id || busyAction != null) return;
    setBusyAction("reject");
    try {
      const result = await dispatch(
        rejectOfferRevisionAsync({
          offerId: offer.id,
          revisionId: rejectableRevision.id,
          change_note: reason,
        })
      );
      if (rejectOfferRevisionAsync.rejected.match(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result.payload,
            result.payload?.missing
              ? "offers.approval.api_unavailable"
              : "offers.approval.reject_failed"
          )
        );
        return;
      }
      toast.success(
        t("offers.approval.reject_success", {
          number: rejectableRevision.revision_number,
        })
      );
      setRejectDialogOpen(false);
      setRejectReason("");
      setRejectReasonError("");
      await refreshAfterMutation();
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.approval.reject_failed")
      );
    } finally {
      setBusyAction(null);
    }
  };

  const handleAcceptConfirm = async () => {
    if (!offer?.id || !acceptableRevision?.id || busyAction != null) return;
    setBusyAction("accept");
    try {
      const result = await dispatch(
        acceptOfferAsync({
          id: offer.id,
          revision_id: acceptableRevision.id,
        })
      );
      if (acceptOfferAsync.rejected.match(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result.payload,
            "offers.history.accept_failed"
          )
        );
        return;
      }
      toast.success(
        t("offers.history.accept_success", {
          number: acceptableRevision.revision_number,
        })
      );
      setConfirmAcceptOpen(false);
      await refreshAfterMutation();
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.history.accept_failed")
      );
    } finally {
      setBusyAction(null);
    }
  };

  if (!revisionsEnabled) {
    return (
      <div className="rounded-lg bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("offers.approval.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("offers.approval.unavailable")}
          </p>
        </div>
      </div>
    );
  }

  if (revisionsLoading && revisions.length === 0) {
    return <ResourceLoading />;
  }

  if (revisionsError && revisions.length === 0) {
    return (
      <ResourceError
        errors={[
          isOfferNotAuthorized({ error: revisionsError })
            ? t("offers.errors.not_authorized")
            : t("offers.history.revisions_error"),
        ]}
        onRetry={loadRevisions}
      />
    );
  }

  if (!draftRevision && !latestLocked) {
    return (
      <div className="rounded-lg bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("offers.approval.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("offers.approval.subtitle")}
          </p>
        </div>
        <div className="px-6 py-10 text-center text-gray-500">
          <p>{t("offers.approval.empty")}</p>
          <p className="mt-2 text-sm">{t("offers.approval.empty_hint")}</p>
        </div>
      </div>
    );
  }

  const focusRevision = draftRevision || latestLocked;
  const isPendingReview = focusRevision?.status === "pending_review";
  const isInternallyApproved =
    latestLocked?.status === "approved" || Boolean(latestLocked?.approved_at);
  const reviewerLabel =
    focusRevision?.reviewer?.displayTitle ||
    focusRevision?.reviewer?.name ||
    null;
  const warningToneClass = {
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  const statusChipClass = isPendingReview
    ? "rounded-md bg-amber-50 px-2 py-1 text-amber-900 ring-1 ring-amber-200"
    : "rounded-md bg-[#EAF3FD] px-2 py-1 text-[#0B3A78] ring-1 ring-[#BFDCF7]";
  const approverChipLabel = isInternallyApproved
    ? t("offers.approval.approver_done")
    : isPendingReview
      ? t("offers.approval.approver_pending")
      : draftRevision
        ? t("offers.approval.approver_idle")
        : t("offers.approval.approver_idle");
  const approverChipClass = isInternallyApproved
    ? "bg-green-50 text-green-800"
    : isPendingReview
      ? "bg-amber-50 text-amber-800"
      : draftRevision
        ? "bg-slate-100 text-slate-600"
        : "bg-slate-100 text-slate-600";

  return (
    <div className="flex flex-col gap-4 overflow-auto p-5 lg:flex-row lg:items-start">
      <div className="min-w-0 flex-1 space-y-4">
        <div className="rounded-[10px] border border-slate-200 bg-white p-4">
          <h3 className="text-[13px] font-[650] text-slate-900">
            {t("offers.approval.title")}
          </h3>

          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[12.5px]">
            <span className="rounded-md bg-slate-50 px-2 py-1 ring-1 ring-slate-200">
              {focusRevision?.revision_number != null
                ? t("offers.history.revision_number", {
                    number: focusRevision.revision_number,
                  })
                : t("offers.info.none")}
            </span>
            {focusRevision?.status && (
              <span className={statusChipClass}>
                {revisionStatusLabel(t, focusRevision.status)}
              </span>
            )}
            {acceptedRevisionId && (
              <span className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2 py-1 text-green-800 ring-1 ring-green-200">
                <CheckCircleIcon className="h-3.5 w-3.5" />
                {t("offers.history.is_accepted")}
              </span>
            )}
          </div>
        </div>

        {/* —— Intern approval —— */}
        <section className="rounded-[10px] border border-slate-200 bg-white p-4">
          <h4 className="text-[13px] font-[650] text-slate-900">
            {t("offers.approval.section_internal_title")}
          </h4>
          <p className="mt-1 text-[12px] text-slate-500">
            {t("offers.approval.section_internal_subtitle")}
          </p>

          <div className="mt-4 flex flex-col gap-2">
            {warnings.map((warning) => (
              <div
                key={warning.id}
                className={`flex items-center gap-2 rounded-[7px] border px-2.5 py-2 text-[12.5px] ${
                  warningToneClass[warning.tone] || warningToneClass.amber
                }`}
              >
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                <span>{warning.text}</span>
              </div>
            ))}
            {isPendingReview && (
              <div className="rounded-[7px] border border-amber-200 bg-amber-50 px-2.5 py-2 text-[12.5px] text-amber-900">
                {reviewerLabel
                  ? t("offers.approval.pending_review_hint_reviewer", {
                      reviewer: reviewerLabel,
                    })
                  : t("offers.approval.pending_review_hint")}
              </div>
            )}
            {isInternallyApproved && !approvableRevision && (
              <div className="rounded-[7px] border border-green-200 bg-green-50 px-2.5 py-2 text-[12.5px] text-green-900">
                {t("offers.approval.approved_locked_hint")}
              </div>
            )}
            {isInternallyApproved && approvableRevision && (
              <div className="rounded-[7px] border border-green-200 bg-green-50 px-2.5 py-2 text-[12.5px] text-green-900">
                {t("offers.approval.approved_at_hint")}
              </div>
            )}
          </div>

          <div className="mt-4">
            <h5 className="mb-2 text-[13px] font-[650] text-slate-900">
              {t("offers.approval.approvers_title")}
            </h5>
            <p className="mb-2 text-[11px] text-slate-400">
              {t("offers.approval.approvers_one_step_note")}
            </p>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-[12.5px] text-slate-700">
                <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#EAF3FD] text-[11px] font-semibold text-[#1463C7]">
                  {(
                    reviewerLabel || t("offers.approval.approver_unassigned")
                  ).slice(0, 1)}
                </span>
                <span className="flex-1">
                  {reviewerLabel
                    ? `${reviewerLabel} — ${t("offers.approval.approver_role")}`
                    : t("offers.approval.approver_unassigned")}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${approverChipClass}`}
                >
                  {approverChipLabel}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <RoleOnly role="offers">
              <button
                type="button"
                onClick={() => setConfirmApproveOpen(true)}
                disabled={!approvableRevision?.id || busyAction != null}
                className="inline-flex items-center rounded-[7px] bg-[#1463C7] px-3.5 py-2 text-[12.5px] font-semibold text-white hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
              >
                <CheckCircleIcon className="mr-1.5 h-4 w-4" />
                {busyAction === "approve"
                  ? t("Loading...")
                  : t("offers.approval.approve")}
              </button>
            </RoleOnly>
            <RoleOnly role="offers">
              <button
                type="button"
                onClick={() => {
                  setRejectReason("");
                  setRejectReasonError("");
                  setRejectDialogOpen(true);
                }}
                disabled={!rejectableRevision?.id || busyAction != null}
                className="inline-flex items-center rounded-[7px] bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <XCircleIcon className="mr-1.5 h-4 w-4" />
                {busyAction === "reject"
                  ? t("Loading...")
                  : t("offers.approval.reject")}
              </button>
            </RoleOnly>
            {typeof handleSubmitForReview === "function" && (
              <RoleOnly role="offers">
                <button
                  type="button"
                  onClick={handleSubmitForReview}
                  disabled={busyAction != null || !approvableRevision?.id}
                  className="inline-flex items-center rounded-[7px] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#1463C7] ring-1 ring-inset ring-[#BFDCF7] hover:bg-[#EAF3FD] disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="mr-1.5 h-4 w-4" />
                  {t("offers.approval.send")}
                </button>
              </RoleOnly>
            )}
            {needsReopen && typeof onCreateRevision === "function" && (
              <RoleOnly role="offers">
                <button
                  type="button"
                  onClick={onCreateRevision}
                  disabled={busyAction != null}
                  className="inline-flex items-center rounded-[7px] bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50"
                >
                  <ArrowPathIcon className="mr-1.5 h-4 w-4" />
                  {t("offers.approval.reopen_cta")}
                </button>
              </RoleOnly>
            )}
          </div>
        </section>

        {/* —— Customer approval —— */}
        <section className="rounded-[10px] border border-slate-200 bg-white p-4">
          <h4 className="text-[13px] font-[650] text-slate-900">
            {t("offers.approval.section_customer_title")}
          </h4>
          <p className="mt-1 text-[12px] text-slate-500">
            {t("offers.approval.section_customer_subtitle")}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <RoleOnly role="offers">
              <button
                type="button"
                onClick={() => setConfirmAcceptOpen(true)}
                disabled={!acceptableRevision?.id || busyAction != null}
                className="inline-flex items-center rounded-[7px] bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busyAction === "accept"
                  ? t("Loading...")
                  : t("offers.approval.accept")}
              </button>
            </RoleOnly>
            {typeof onSendOffer === "function" && (
              <RoleOnly role="offers">
                <button
                  type="button"
                  onClick={onSendOffer}
                  disabled={busyAction != null}
                  className="inline-flex items-center rounded-[7px] bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50"
                >
                  {t("offers.banner.send_offer")}
                </button>
              </RoleOnly>
            )}
          </div>

          <p className="mt-3 text-xs text-slate-500">
            {t("offers.approval.workflow_hint")}
          </p>
        </section>
      </div>

      <aside className="w-full flex-shrink-0 rounded-[10px] border border-slate-200 bg-white p-4 lg:w-[280px]">
        <h3 className="mb-2.5 text-[13px] font-[650] text-slate-900">
          {t("offers.approval.key_figures")}
        </h3>
        <dl className="flex flex-col gap-2 text-[12.5px]">
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">{t("offers.overview.kpi.total")}</dt>
            <dd className="font-semibold tabular-nums text-slate-900">
              {formatMoney(priceFormatter, offerTotal)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">{t("offers.overview.kpi.cost")}</dt>
            <dd className="font-semibold tabular-nums text-slate-900">
              {formatMoney(priceFormatter, costTotal)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">
              {t("offers.overview.kpi.contribution_margin_percent")}
            </dt>
            <dd
              className={`font-semibold tabular-nums ${
                !Number.isNaN(parseFloat(dgPercent)) &&
                parseFloat(dgPercent) < DG_WARNING_THRESHOLD
                  ? "text-amber-600"
                  : "text-slate-900"
              }`}
            >
              {formatPercent(dgPercent)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">{t("offers.approval.discount")}</dt>
            <dd className="font-semibold tabular-nums text-slate-900">
              {formatMoney(priceFormatter, discountTotal)}
            </dd>
          </div>
        </dl>
      </aside>

      <PMDialog
        open={confirmApproveOpen}
        onClose={() => {
          if (busyAction === "approve") return;
          setConfirmApproveOpen(false);
        }}
        title={t("offers.approval.confirm_approve_title")}
        size="md"
        closeOnOutsideClick={busyAction !== "approve"}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            {t("offers.approval.confirm_approve_body", {
              number: approvableRevision?.revision_number ?? "—",
            })}
          </p>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setConfirmApproveOpen(false)}
              disabled={busyAction === "approve"}
              className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {t("Close")}
            </button>
            <button
              type="button"
              onClick={handleApproveConfirm}
              disabled={busyAction === "approve" || !approvableRevision?.id}
              className="inline-flex justify-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busyAction === "approve"
                ? t("Loading...")
                : t("offers.approval.confirm_approve_submit")}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={rejectDialogOpen}
        onClose={closeRejectDialog}
        title={t("offers.approval.reject_dialog_title")}
        size="md"
        closeOnOutsideClick={busyAction !== "reject"}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            {t("offers.approval.reject_dialog_body", {
              number: rejectableRevision?.revision_number ?? "—",
            })}
          </p>
          <div>
            <label
              htmlFor="offer-reject-reason"
              className="mb-1.5 block text-sm font-semibold text-slate-700"
            >
              {t("offers.approval.reject_reason_label")}
            </label>
            <textarea
              id="offer-reject-reason"
              rows={4}
              value={rejectReason}
              onChange={(e) => {
                setRejectReason(e.target.value);
                if (rejectReasonError && e.target.value.trim()) {
                  setRejectReasonError("");
                }
              }}
              placeholder={t("offers.approval.reject_reason_placeholder")}
              className={`w-full rounded-lg border px-3 py-2 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1463C7] ${
                rejectReasonError
                  ? "border-rose-400 focus:ring-rose-400"
                  : "border-slate-300"
              }`}
              disabled={busyAction === "reject"}
            />
            {rejectReasonError ? (
              <p className="mt-1.5 text-sm text-rose-600">{rejectReasonError}</p>
            ) : null}
          </div>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={closeRejectDialog}
              disabled={busyAction === "reject"}
              className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {t("Close")}
            </button>
            <button
              type="button"
              onClick={handleRejectConfirm}
              disabled={busyAction === "reject" || !rejectableRevision?.id}
              className="inline-flex justify-center rounded-lg border border-transparent bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busyAction === "reject"
                ? t("Loading...")
                : t("offers.approval.reject_confirm")}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={confirmAcceptOpen}
        onClose={() => {
          if (busyAction === "accept") return;
          setConfirmAcceptOpen(false);
        }}
        title={t("offers.approval.confirm_accept_title")}
        size="md"
        closeOnOutsideClick={busyAction !== "accept"}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            {t("offers.approval.confirm_accept_body", {
              number: acceptableRevision?.revision_number ?? "—",
            })}
          </p>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setConfirmAcceptOpen(false)}
              disabled={busyAction === "accept"}
              className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {t("Close")}
            </button>
            <button
              type="button"
              onClick={handleAcceptConfirm}
              disabled={busyAction === "accept" || !acceptableRevision?.id}
              className="inline-flex justify-center rounded-lg border border-transparent bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busyAction === "accept"
                ? t("Loading...")
                : t("offers.approval.confirm_accept_submit")}
            </button>
          </div>
        </div>
      </PMDialog>
    </div>
  );
}
